import * as XLSX from 'xlsx';

/**
 * Export clients data to Excel file
 * @param {Array} clients - Array of client objects to export
 * @param {String} filename - Optional filename for the exported file
 */
export const exportClientsToExcel = (clients, filename = 'clients_export.xlsx') => {
  if (!clients || clients.length === 0) {
    console.warn('No clients data to export');
    return;
  }

  // Format the data for Excel
  const formattedData = clients.map(client => ({
    'Company Name': client.companyName || '',
    'Website': client.websiteUrl || '',
    'LinkedIn': client.linkedInUrl || '',
    'Industry': client.industry || '',
    'Relationship Type': client.relationshipType || '',
    'Lead Status': client.leadStatus || '',
    'Lead Source': client.leadSource || '',
    'Company Stage': client.companyStage || '',
    'Legal Type': client.legalType || '',
    'Employee Count': client.employeeCount || '',
    'Annual Revenue': client.annualRevenue || '',
    'Primary Contact': client.primaryContact || '',
    'Job Title': client.jobTitle || '',
    'Email': client.email || '',
    'Phone': client.phone || '',
    'Contact Owner': client.contactOwner || '',
    'Hiring Manager': client.hiringManager || '',
    'Role': client.role || '',
    'POC Name': client.pocName || '',
    'POC Job Title': client.pocJobTitle || '',
    'POC Email': client.pocEmail || '',
    'POC Phone': client.pocPhone || '',
    'Billing Street': client.billingStreet || '',
    'Billing City': client.billingCity || '',
    'Billing State': client.billingState || '',
    'Billing Country': client.billingCountry || '',
    'Billing Zip Code': client.billingZipCode || '',
    'Shipping Street': client.shippingStreet || '',
    'Shipping City': client.shippingCity || '',
    'Shipping State': client.shippingState || '',
    'Shipping Country': client.shippingCountry || '',
    'Shipping Zip Code': client.shippingZipCode || '',
    'Tax ID Type': client.taxIdType || '',
    'Tax ID Number': client.taxIdNumber || '',
    'Payment Terms': client.paymentTerms || '',
    'Currency': client.currency || '',
    'Do Not Call': client.doNotCall ? 'Yes' : 'No',
    'Do Not Email': client.doNotEmail ? 'Yes' : 'No',
    'Created At': client.createdAt ? new Date(client.createdAt).toLocaleString() : '',
    'Updated At': client.updatedAt ? new Date(client.updatedAt).toLocaleString() : '',
    'Created By': client.createdBy || '',
    'ID': client.id || ''
  }));

  // Create a new workbook
  const worksheet = XLSX.utils.json_to_sheet(formattedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients');

  // Set column widths
  const columnWidths = [
    { wch: 25 }, // Company Name
    { wch: 30 }, // Website
    { wch: 30 }, // LinkedIn
    { wch: 15 }, // Industry
    { wch: 18 }, // Relationship Type
    { wch: 15 }, // Lead Status
    { wch: 18 }, // Lead Source
    { wch: 15 }, // Company Stage
    { wch: 20 }, // Legal Type
    { wch: 15 }, // Employee Count
    { wch: 18 }, // Annual Revenue
    { wch: 20 }, // Primary Contact
    { wch: 20 }, // Job Title
    { wch: 30 }, // Email
    { wch: 15 }, // Phone
    { wch: 20 }, // Contact Owner
    { wch: 20 }, // Hiring Manager
    { wch: 12 }, // Role
    { wch: 20 }, // POC Name
    { wch: 20 }, // POC Job Title
    { wch: 30 }, // POC Email
    { wch: 15 }, // POC Phone
    { wch: 30 }, // Billing Street
    { wch: 15 }, // Billing City
    { wch: 15 }, // Billing State
    { wch: 15 }, // Billing Country
    { wch: 12 }, // Billing Zip Code
    { wch: 30 }, // Shipping Street
    { wch: 15 }, // Shipping City
    { wch: 15 }, // Shipping State
    { wch: 15 }, // Shipping Country
    { wch: 12 }, // Shipping Zip Code
    { wch: 15 }, // Tax ID Type
    { wch: 20 }, // Tax ID Number
    { wch: 15 }, // Payment Terms
    { wch: 10 }, // Currency
    { wch: 12 }, // Do Not Call
    { wch: 12 }, // Do Not Email
    { wch: 20 }, // Created At
    { wch: 20 }, // Updated At
    { wch: 20 }, // Created By
    { wch: 10 }  // ID
  ];
  worksheet['!cols'] = columnWidths;

  // Generate and download the file
  XLSX.writeFile(workbook, filename);
};

/**
 * Export clients data to CSV file
 * @param {Array} clients - Array of client objects to export
 * @param {String} filename - Optional filename for the exported file
 */
export const exportClientsToCSV = (clients, filename = 'clients_export.csv') => {
  if (!clients || clients.length === 0) {
    console.warn('No clients data to export');
    return;
  }

  // Format the data for CSV
  const formattedData = clients.map(client => ({
    'Company Name': client.companyName || '',
    'Website': client.websiteUrl || '',
    'Industry': client.industry || '',
    'Relationship Type': client.relationshipType || '',
    'Lead Status': client.leadStatus || '',
    'Lead Source': client.leadSource || '',
    'Primary Contact': client.primaryContact || '',
    'Email': client.email || '',
    'Phone': client.phone || '',
    'Contact Owner': client.contactOwner || '',
    'Created At': client.createdAt ? new Date(client.createdAt).toLocaleString() : ''
  }));

  // Create CSV content
  const headers = Object.keys(formattedData[0]);
  const csvContent = [
    headers.join(','),
    ...formattedData.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escape values that contain commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');

  // Create and download the file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export selected clients with custom columns
 * @param {Array} clients - Array of client objects to export
 * @param {Array} columns - Array of column keys to include in export
 * @param {String} filename - Optional filename for the exported file
 */
export const exportClientsWithCustomColumns = (clients, columns, filename = 'clients_custom_export.xlsx') => {
  if (!clients || clients.length === 0) {
    console.warn('No clients data to export');
    return;
  }

  // Column label mapping
  const columnLabels = {
    companyName: 'Company Name',
    websiteUrl: 'Website',
    linkedInUrl: 'LinkedIn',
    industry: 'Industry',
    relationshipType: 'Relationship Type',
    leadStatus: 'Lead Status',
    leadSource: 'Lead Source',
    companyStage: 'Company Stage',
    legalType: 'Legal Type',
    employeeCount: 'Employee Count',
    annualRevenue: 'Annual Revenue',
    primaryContact: 'Primary Contact',
    jobTitle: 'Job Title',
    email: 'Email',
    phone: 'Phone',
    contactOwner: 'Contact Owner',
    hiringManager: 'Hiring Manager',
    pocName: 'POC Name',
    pocJobTitle: 'POC Job Title',
    pocEmail: 'POC Email',
    pocPhone: 'POC Phone',
    billingCity: 'Billing City',
    billingState: 'Billing State',
    billingCountry: 'Billing Country',
    paymentTerms: 'Payment Terms',
    currency: 'Currency',
    createdAt: 'Created At',
    updatedAt: 'Updated At'
  };

  // Format the data with only selected columns
  const formattedData = clients.map(client => {
    const row = {};
    columns.forEach(col => {
      const label = columnLabels[col] || col;
      let value = client[col] || '';
      
      // Format dates
      if ((col === 'createdAt' || col === 'updatedAt') && value) {
        value = new Date(value).toLocaleString();
      }
      
      row[label] = value;
    });
    return row;
  });

  // Create and download the file
  const worksheet = XLSX.utils.json_to_sheet(formattedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients');
  XLSX.writeFile(workbook, filename);
};
